import { ZERO_MARGINS, ZERO_SPATIAL_RECTANGLE } from "@pdfking/math/constants"
import { FullMethodDispatcher } from "@pdfking/tfunc"

import { NO_COLOR, NO_FILE, NO_LAYER } from "#constants"
import type { CompletePage, Page } from "#public"
import type { PlottingContext } from "#public"
import type { PlotGroup } from "#public"
import type { PlotMedia } from "#public"
import type { PlotPath } from "#public"
import type { PlotRect } from "#public"
import type { PlotText } from "#public"
import type { PageNumber } from "#public"
import type { PageShape } from "#public"
import type { PlotShape } from "#public"

import { PlotFunction } from "../types/plot/PlotFunction"
import { MatrixIdentity } from "./MatrixIdentity"

const createShape = FullMethodDispatcher<[PageShape, PlottingContext], PlotShape>({
  // A group's children are completed too, so a partial page may nest as deeply
  // as it likes and every leaf still comes back whole.
  [PlotFunction.PLOT_GROUP]: ({ enum: _, ...rest }, context): PlotGroup => ({
    matrix: MatrixIdentity(),
    ...rest,
    shapes: (rest.shapes ?? []).map(shape => createShape(shape, context)),
    enum: PlotFunction.PLOT_GROUP,
  }),
  [PlotFunction.PLOT_MEDIA]: ({ enum: _, ...rest }, _context): PlotMedia => ({
    layer: NO_LAYER,
    mask: ZERO_MARGINS,
    media_matrix: MatrixIdentity(),
    position: ZERO_SPATIAL_RECTANGLE,
    filename: NO_FILE,
    page_number: 1 as PageNumber,
    print_layer: { enum: "print", order: 0 },
    ...rest,
    enum: PlotFunction.PLOT_MEDIA,
  }),
  [PlotFunction.PLOT_PATH]: ({ enum: _, ...rest }, context): PlotPath => ({
    closed: false,
    fill_color: NO_COLOR,
    layer: NO_LAYER,
    points: [],
    stroke_color: NO_COLOR,
    stroke_width: context.stroke_width,
    ...rest,
    enum: PlotFunction.PLOT_PATH,
  }),
  [PlotFunction.PLOT_RECT]: ({ enum: _, ...rest }, context): PlotRect => ({
    fill_color: NO_COLOR,
    layer: NO_LAYER,
    position: ZERO_SPATIAL_RECTANGLE,
    stroke_color: NO_COLOR,
    stroke_width: context.stroke_width,
    ...rest,
    enum: PlotFunction.PLOT_RECT,
  }),
  [PlotFunction.PLOT_TEXT]: ({ enum: _, ...rest }, context): PlotText => ({
    fill_color: NO_COLOR,
    font_name: context.font_family,
    font_size: context.font_size,
    horizontal_align: context.horizontal_align,
    layer: NO_LAYER,
    position: ZERO_SPATIAL_RECTANGLE,
    stroke_color: NO_COLOR,
    stroke_width: context.stroke_width,
    text: "",
    text_color: NO_COLOR,
    vertical_align: context.vertical_align,
    ...rest,
    enum: PlotFunction.PLOT_TEXT,
  }),
})

export const CreateCompletePage = (page: Page, context: PlottingContext): CompletePage => {
  const shapes = (page.shapes ?? []).map(shape => createShape(shape, context))
  return {
    bleed: context.page_bleed,
    ...ZERO_SPATIAL_RECTANGLE,
    ...page,
    shapes,
  }
}
